package com.eladfinish.bible.contest.data.common

import com.eladfinish.bible.contest.data.common.stringResolver.WebStrings
import dev.icerock.moko.resources.desc.StringDesc
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.await
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLTextAreaElement
import kotlin.js.Promise

/**
 * Web's actual implementation of the ClipboardManager, backed by the async Clipboard API
 * (`navigator.clipboard`), which browsers expose on secure origins only. Writing falls back to the
 * legacy `document.execCommand("copy")` where that API is missing or rejects.
 */
actual class MyClipboardManager {

    actual fun copyToClipboard(text: String): Boolean {
        if (text.isBlank()) {
            return false
        }
        val clipboard = window.navigator.asDynamic().clipboard
        if (clipboard?.writeText == null) {
            return copyWithExecCommand(text)
        }
        // The browser answers asynchronously, after this function has returned, so a refusal
        // can't be reported through the return value; retry it the legacy way instead.
        clipboard.writeText(text).unsafeCast<Promise<Unit>>().catch { copyWithExecCommand(text) }
        return true
    }

    actual fun copyToClipboard(stringDesc: StringDesc): Boolean =
        copyToClipboard(WebStrings.resolveOrEmpty(stringDesc))

    // A browser only hands out clipboard contents asynchronously — see readText().
    actual fun getText(): String? = null

    actual suspend fun readText(): String? {
        val clipboard = window.navigator.asDynamic().clipboard
        if (clipboard?.readText == null) return null
        return try {
            clipboard.readText().unsafeCast<Promise<String>>().await()
        } catch (_: Throwable) {
            // The user declined the browser's paste prompt, or the page isn't focused.
            null
        }
    }

    private fun copyWithExecCommand(text: String): Boolean {
        val previouslyFocused = document.activeElement as? HTMLElement
        val textArea = (document.createElement("textarea") as HTMLTextAreaElement).apply {
            value = text
            style.position = "fixed"
            style.opacity = "0"
        }
        document.body?.appendChild(textArea)
        textArea.select()
        val copied = try {
            document.asDynamic().execCommand("copy").unsafeCast<Boolean>()
        } catch (_: Throwable) {
            false
        }
        textArea.remove()
        previouslyFocused?.focus()
        return copied
    }
}
